import appLogger from '@/utils/logger';
import BotAlert from '@/components/BotAlert';
import { createPortal } from 'react-dom';
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useUserContext } from '@/contexts/UserProvider';
import { useGetBotData } from '@/hooks/useBot';
import { updateBot, uploadFileCustomize } from '@/services/bots';
import { ChromePicker } from 'react-color';
import {
  Bot, MessageSquare, Palette, Images, MousePointerClick, Type, RotateCcw, Sparkles,
} from 'lucide-react';
import { FormField, FormInput } from '@/components/audiences';
import {
  SectionCard, StickyActionBar, ImageUploadField, ChatbotPreviewPanel, PreviewTabLayout,
} from './shared';
import CtaFlowsEditor, { type CtaFlow } from './customize/CtaFlowsEditor';

interface ICustomizeProps {
  token: string;
  updateBotList: () => void;
  primaryColor: string;
  setPrimaryColor: Dispatch<SetStateAction<string>>;
}

const Customize: FC<ICustomizeProps> = ({ token, primaryColor, setPrimaryColor }) => {
  const { vendorConfig } = useUserContext();
  const { data: botData, refetch: refetchBotData, isLoading: isBotDataLoading, isFetching: isBotDataFetching } = useGetBotData(token);
  const previewGrant: string | undefined = botData?.previewGrant;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Once bot data has been loaded, keep iframe rendered even during React Query refetches.
  // This prevents the iframe from disappearing when botData?.data is briefly undefined
  // (e.g., when query key changes due to token/bot state transitions).
  const hasBotDataLoadedRef = useRef(false);

  const [name, setName] = useState<string>('Bot');
  const [botName, setBotName] = useState<string>('Assistant');
  const [greetingMessage1, setGreetingMessage1] = useState<string>('Hi! 👋');
  const [greetingMessage2, setGreetingMessage2] = useState<string>('How can we help?');
  const [welcomeMessage, setWelcomeMessage] = useState<string>('Welcome! 👋 What can I help you with today?');
  const [customEmailMessage, setCustomEmailMessage] = useState('');
  const [chatHeight, setChatHeight] = useState<number>(700);
  const [fontSize, setFontSize] = useState<number>(16);
  const [fontFamily, setFontFamily] = useState<string>('');
  const [fontWeight, setFontWeight] = useState<string>('');
  // Empty = "Auto" (widget falls back to #f5f5f0 / #ffffff). Must start empty so a
  // saved Auto reset round-trips correctly instead of reverting to the default.
  const [botBubbleColor, setBotBubbleColor] = useState<string>('');
  const [chatBgColor, setChatBgColor] = useState<string>('');
  const [launchBgColor, setLaunchBgColor] = useState<string>('');
  const [userBubbleColor, setUserBubbleColor] = useState<string>('');
  const [headerColor, setHeaderColor] = useState<string>('');
  // popupColor / popupTextColor are owned exclusively by the Pre-Chat Popup tab.
  const [headerTextColor, setHeaderTextColor] = useState<string>('');
  const [botTextColor, setBotTextColor] = useState<string>('');
  const [linkColor, setLinkColor] = useState<string>('');
  const [userTextColor, setUserTextColor] = useState<string>('');
  const [ctaColor, setCtaColor] = useState<string>('');
  const [ctaTextColor, setCtaTextColor] = useState<string>('');
  const [suggestionColor, setSuggestionColor] = useState<string>('');
  const [suggestionTextColor, setSuggestionTextColor] = useState<string>('');
  const [suggestionBgColor, setSuggestionBgColor] = useState<string>('');
  const [suggestionLabelColor, setSuggestionLabelColor] = useState<string>('');
  const [showOnlineStatus, setShowOnlineStatus] = useState<boolean>(true);
  const [activePicker, setActivePicker] = useState<string | null>(null);
  const [pickerPos, setPickerPos] = useState<{ top: number; left: number; above: boolean } | null>(null);
  const pickerBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const [botIconFile, setBotIconFile] = useState<File | null>(null);
  const [userIconFile, setUserIconFile] = useState<File | null>(null);
  const [launchIconFile, setLaunchIconFile] = useState<File | null>(null);
  const [botIcon, setBotIconSrc] = useState('');
  const [launchIconSrc, setLaunchIconSrc] = useState('');
  const [userIcon, setUserIconSrc] = useState('');

  const [ctaFlows, setCtaFlows] = useState<CtaFlow[]>([]);
  const [alert, setAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  // Stable, persisted per-bot preview visitor identity. The widget keys its socket room, online
  // presence, and — critically — any in-flight HUMAN HANDOFF by this userIdToken. Regenerating it
  // mid-session (previously done on every Save) orphaned a live handoff: the agent's Slack/Teams
  // replies were emitted to the OLD token's socket room while the reloaded widget listened on a new
  // one, so the visitor saw nothing. Persist it in localStorage so it survives re-renders, Saves,
  // tab switches, and reloads — matching how a real embedded widget keeps a stable identity. Only an
  // explicit "Reset Preview" starts a new visitor. (#4037)
  const previewTokenKey = `mb_preview_uidtoken_${token}`;
  const [previewUserIdToken, setPreviewUserIdToken] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const existing = window.localStorage.getItem(previewTokenKey);
      if (existing) return existing;
      const fresh = uuidv4();
      window.localStorage.setItem(previewTokenKey, fresh);
      return fresh;
    }
    return uuidv4();
  });

  const [isLoading, setIsLoading] = useState(false);
  // #2637 data-loss guard: block Save until this bot's saved appearance has hydrated
  // into the form, so a Save before botData loads can't write defaults over good data.
  const [hydrated, setHydrated] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);

  // Track if we've completed React Strict Mode's initial mount/unmount cycle.
  // In development, Strict Mode intentionally unmounts and remounts components
  // to help detect side effects; this flag prevents the iframe from loading
  // during the first (immediately-unmounted) mount.
  const isStrictModeStableRef = useRef(false);
  const [isReadyToRenderIframe, setIsReadyToRenderIframe] = useState(false);

  // Compute demoUrl synchronously using useMemo to avoid race conditions with React Strict Mode.
  // (A prior useState + useEffect version produced async updates that conflicted with Strict
  // Mode's double-invoke cycle, leading to iframe requests being canceled.)
  const demoUrl = useMemo(() => {
    if (!vendorConfig?.vendorId || !token) return '';
    const chatbotUrl = process.env.NEXT_PUBLIC_CHATBOT_URL;
    const urlString = typeof chatbotUrl === 'string' ? chatbotUrl : String(chatbotUrl || '');
    if (urlString && urlString.trim() !== '' && urlString !== 'undefined') {
      const baseUrl = urlString.trim();
      const separator = baseUrl.includes('?') ? '&' : '?';
      // GH#6239: previewGrant is minted by the authenticated admin-config read and lifts the
      // visitor geo gate for this bot only. Without it, previewing a country-restricted bot
      // from outside its allowlist 404s the iframe's config — including for us.
      const grant = previewGrant ? `&previewGrant=${encodeURIComponent(previewGrant)}` : '';
      return `${baseUrl}${separator}token=${encodeURIComponent(token)}&defaultOpenBot=true&preview=true&userIdToken=${encodeURIComponent(previewUserIdToken)}${grant}`;
    }
    return '';
  }, [token, vendorConfig?.vendorId, previewUserIdToken, previewGrant]);

  const handleResetPreview = () => {
    setIframeLoading(true);
    // Explicit, user-initiated new visitor session: rotate + persist the new identity.
    const fresh = uuidv4();
    if (typeof window !== 'undefined') window.localStorage.setItem(previewTokenKey, fresh);
    setPreviewUserIdToken(fresh);
  };

  // Mark Strict Mode cycle complete after a tick so the iframe only renders once.
  useEffect(() => {
    if (!isStrictModeStableRef.current) {
      isStrictModeStableRef.current = true;
      setTimeout(() => setIsReadyToRenderIframe(true), 0);
    }
  }, []);

  const onHandleSave = async () => {
    // #2637: never persist an un-hydrated form (would write default appearance over
    // saved customizationdata). Belt to the disabled Save button.
    if (!hydrated) return;
    setIsLoading(true);
    try {
      const data = {
        name,
        botName,
        welcomeMessage,
        primaryColor,
        chatHeight,
        fontSize,
        botBubbleColor,
        chatBgColor,
        launchBgColor,
        userBubbleColor,
        headerColor,
        fontFamily,
        fontWeight,
        headerTextColor,
        botTextColor,
        linkColor,
        userTextColor,
        ctaColor,
        ctaTextColor,
        suggestionColor,
        suggestionTextColor,
        suggestionBgColor,
        suggestionLabelColor,
        showOnlineStatus,
        botIcon: { uploadedUri: botIcon },
        userIcon: { uploadedUri: userIcon },
        greetingMessage1,
        greetingMessage2,
        launchIcon: { uploadedUri: launchIconSrc },
        customEmailMessage,
        cta_flows: ctaFlows,
      };

      if (botIconFile !== null) {
        data.botIcon = (await uploadFileCustomize(botIconFile, 'icon')) as { uploadedUri: string };
      }
      if (userIconFile !== null) {
        data.userIcon = (await uploadFileCustomize(userIconFile, 'icon')) as { uploadedUri: string };
      }
      if (launchIconFile !== null) {
        data.launchIcon = (await uploadFileCustomize(launchIconFile, 'icon')) as { uploadedUri: string };
      }

      await updateBot(data, token);

      // Do NOT rotate the preview userIdToken on Save. Appearance changes are pushed to the widget
      // live via the PREVIEW_SETTINGS postMessage, so a reload isn't needed to reflect them — and
      // rotating the token restarts the widget as a NEW visitor, orphaning any in-flight human
      // handoff (the agent's replies would emit to the old token's room). Use "Reset Preview" to
      // deliberately start a fresh visitor. (#4037)
      refetchBotData();

      setIsLoading(false);
      setAlertMessage('The configuration information has been successfully saved!');
      setAlert(true);
    } catch (e) {
      setIsLoading(false);
      appLogger.error('Error saving data:', { data: e });
    }
  };

  // Hydrate all form fields from the latest bot data. Shared by the load effect
  // and the Reset action.
  const populateFromBotData = useCallback(() => {
    if (botData?.data?.status !== true) return;
    const c = botData.data.customizationdata;

    if (c.name) setName(c.name);
    if (c.botName) setBotName(c.botName);
    if (c.greetingMessage1) setGreetingMessage1(c.greetingMessage1);
    if (c.greetingMessage2) setGreetingMessage2(c.greetingMessage2);
    if (c.welcomeMessage) setWelcomeMessage(c.welcomeMessage);
    if (c.primaryColor && c.primaryColor !== primaryColor) setPrimaryColor(c.primaryColor);
    if (c.chatHeight) setChatHeight(c.chatHeight);
    if (c.fontSize) setFontSize(c.fontSize);
    if (c.botBubbleColor !== undefined) setBotBubbleColor(c.botBubbleColor || '');
    if (c.chatBgColor !== undefined) setChatBgColor(c.chatBgColor || '');
    if (c.launchBgColor !== undefined) setLaunchBgColor(c.launchBgColor || '');
    if (c.userBubbleColor !== undefined) setUserBubbleColor(c.userBubbleColor || '');
    if (c.headerColor !== undefined) setHeaderColor(c.headerColor || '');
    if (c.fontFamily !== undefined) setFontFamily(c.fontFamily || '');
    if (c.fontWeight !== undefined) setFontWeight(c.fontWeight || '');
    if (c.headerTextColor !== undefined) setHeaderTextColor(c.headerTextColor || '');
    if (c.botTextColor !== undefined) setBotTextColor(c.botTextColor || '');
    if (c.linkColor !== undefined) setLinkColor(c.linkColor || '');
    if (c.userTextColor !== undefined) setUserTextColor(c.userTextColor || '');
    if (c.ctaColor !== undefined) setCtaColor(c.ctaColor || '');
    if (c.ctaTextColor !== undefined) setCtaTextColor(c.ctaTextColor || '');
    if (c.suggestionColor !== undefined) setSuggestionColor(c.suggestionColor || '');
    if (c.suggestionTextColor !== undefined) setSuggestionTextColor(c.suggestionTextColor || '');
    if (c.suggestionBgColor !== undefined) setSuggestionBgColor(c.suggestionBgColor || '');
    if (c.suggestionLabelColor !== undefined) setSuggestionLabelColor(c.suggestionLabelColor || '');
    if (c.showOnlineStatus !== undefined) setShowOnlineStatus(c.showOnlineStatus !== false);
    setCustomEmailMessage(c.customEmailMessage || '');

    if (c.launchIcon) {
      const v = c.launchIcon;
      setLaunchIconSrc(typeof v === 'string' ? v : v?.uploadedUri || '');
    }
    if (c.botIcon) {
      const v = c.botIcon;
      setBotIconSrc(typeof v === 'string' ? v : v?.uploadedUri || '');
    }
    if (c.userIcon) {
      const v = c.userIcon;
      setUserIconSrc(typeof v === 'string' ? v : v?.uploadedUri || '');
    }
    setBotIconFile(null);
    setUserIconFile(null);
    setLaunchIconFile(null);
    if (Array.isArray(c.cta_flows)) setCtaFlows(c.cta_flows as CtaFlow[]);
    // Form now reflects saved appearance — Save is safe to enable.
    setHydrated(true);
  }, [botData, primaryColor, setPrimaryColor]);

  // #2637: block Save the moment the bot changes, until its data re-hydrates.
  useEffect(() => {
    setHydrated(false);
  }, [token]);

  // Populate form fields when botData loads.
  useEffect(() => {
    populateFromBotData();
  }, [botData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (demoUrl && demoUrl.trim() !== '') setIframeLoading(true);
  }, [demoUrl]);

  // ---- Real-time preview ----
  // Post the current (possibly unsaved) theme settings into the preview iframe so
  // color/font changes reflect instantly without saving. The widget merges these
  // via a whitelisted PREVIEW_SETTINGS handler (no persistence — Save still writes).
  const previewSettings = useMemo(() => ({
    primaryColor,
    headerColor: headerColor || undefined,
    chatBgColor: chatBgColor || undefined,
    launchBgColor: launchBgColor || undefined,
    userBubbleColor: userBubbleColor || undefined,
    botBubbleColor: botBubbleColor || undefined,
    headerTextColor: headerTextColor || undefined,
    botTextColor: botTextColor || undefined,
    linkColor: linkColor || undefined,
    userTextColor: userTextColor || undefined,
    ctaColor: ctaColor || undefined,
    ctaTextColor: ctaTextColor || undefined,
    suggestionColor: suggestionColor || undefined,
    suggestionTextColor: suggestionTextColor || undefined,
    suggestionBgColor: suggestionBgColor || undefined,
    suggestionLabelColor: suggestionLabelColor || undefined,
    fontFamily: fontFamily || undefined,
    fontWeight: fontWeight || undefined,
    fontSize,
    chatHeight,
    botName,
    showOnlineStatus,
    botIcon: { uploadedUri: botIcon },
    userIcon: { uploadedUri: userIcon },
  }), [
    primaryColor, headerColor, chatBgColor, launchBgColor, userBubbleColor, botBubbleColor,
    headerTextColor, botTextColor, linkColor, userTextColor, ctaColor, ctaTextColor,
    suggestionColor, suggestionTextColor, suggestionBgColor, suggestionLabelColor,
    fontFamily, fontWeight, fontSize, chatHeight,
    botName, showOnlineStatus, botIcon, userIcon,
  ]);

  const postPreviewSettings = useCallback(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'PREVIEW_SETTINGS', setting: previewSettings }, '*',
    );
  }, [previewSettings]);

  // Push on every theme change (debounced a touch so colour-drag isn't chatty).
  useEffect(() => {
    const t = setTimeout(postPreviewSettings, 120);
    return () => clearTimeout(t);
  }, [postPreviewSettings]);

  // Re-sync when the widget (re)announces readiness, e.g. after iframe load/reset.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      if (e?.data?.type === 'IFRAME_READY') postPreviewSettings();
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [postPreviewSettings]);

  // Track whether bot data has ever been successfully loaded. Once loaded, keep the
  // ref true even if botData temporarily becomes undefined (React Query refetches,
  // query key changes, or state transitions) so the iframe doesn't flicker away.
  if (botData?.data) hasBotDataLoadedRef.current = true;

  const shouldRenderIframe = !!(
    demoUrl &&
    demoUrl.trim() !== '' &&
    isReadyToRenderIframe &&
    hasBotDataLoadedRef.current
  );

  const showIconError = (message: string) => {
    setAlertMessage(message);
    setAlert(true);
  };

  return (
    <>
      <PreviewTabLayout
        preview={(
          <ChatbotPreviewPanel
            demoUrl={demoUrl}
            shouldRenderIframe={shouldRenderIframe}
            iframeLoading={iframeLoading}
            chatHeight={chatHeight}
            iframeRef={iframeRef}
            onReset={handleResetPreview}
            onIframeSettled={() => setIframeLoading(false)}
            debug={{
              isReadyToRenderIframe,
              isFetching: isBotDataLoading || isBotDataFetching,
              hasData: !!botData?.data,
              hasToken: !!token,
              vendorId: vendorConfig?.vendorId,
            }}
          />
        )}
        footer={(
          <StickyActionBar
            onReset={populateFromBotData}
            onSave={() => { void onHandleSave(); }}
            saving={isLoading}
            saveDisabled={isLoading || !name.trim() || !hydrated}
            resetDisabled={isLoading}
            saveLabel="Save"
          />
        )}
      >
        <div className="space-y-6">
          {/* Basic Information */}
          <SectionCard
            icon={Bot}
            title="Basic Information"
            subtitle="The bot's name and the name shown in the chat header."
          >
            <FormInput
              label="Chatbot Name"
              required
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Bot"
            />
            <FormInput
              label="Bot Display Name"
              type="text"
              value={botName}
              onChange={(e) => setBotName(e.target.value)}
              placeholder="Assistant"
              helperText='The name shown in the chat header (e.g., "Assistant", "Support Bot")'
            />
          </SectionCard>

          {/* Messages */}
          <SectionCard
            icon={MessageSquare}
            title="Messages"
            subtitle="The greeting and first messages visitors see."
          >
            <FormInput
              label="Greeting Message"
              type="text"
              value={greetingMessage2}
              onChange={(e) => setGreetingMessage2(e.target.value)}
              placeholder="Hello"
              helperText="The greeting message in the chatbot Home tab"
            />
            <FormInput
              label="Welcome Message"
              type="text"
              value={welcomeMessage}
              onChange={(e) => setWelcomeMessage(e.target.value)}
              placeholder="Welcome! 👋 What can I help you with today?"
              helperText="The first message that the users will see in the chatbot"
            />
            <FormInput
              label="Custom Email Message"
              type="text"
              value={customEmailMessage}
              onChange={(e) => setCustomEmailMessage(e.target.value)}
              placeholder="Please provide us your email for further information"
            />
          </SectionCard>

          {/* Colors & Theme */}
          <SectionCard
            icon={Palette}
            title="Colors & Theme"
            subtitle="Surfaces marked Auto follow Primary; text colors auto-pick a readable (ADA-safe) value. Set a custom color to override."
          >
            {(() => {
              type Swatch = {
                key: string; label: string; desc: string;
                effective: string; set: (v: string) => void; reset: () => void;
                isAuto: boolean; group: 'surface' | 'text';
              };
              const swatches: Swatch[] = [
                // Surface colors
                { key: 'primaryColor',    label: 'Primary',        desc: 'Header, buttons, send icon', effective: primaryColor,                  set: (v) => setPrimaryColor(v),    reset: () => {},                     isAuto: false,             group: 'surface' },
                { key: 'headerColor',     label: 'Header',         desc: 'Chat header bar',                   effective: headerColor || primaryColor,   set: (v) => setHeaderColor(v),     reset: () => setHeaderColor(''),     isAuto: !headerColor,      group: 'surface' },
                { key: 'chatBgColor',     label: 'Background',     desc: 'Messages area',                     effective: chatBgColor || '#ffffff',      set: (v) => setChatBgColor(v),     reset: () => setChatBgColor(''),     isAuto: !chatBgColor,      group: 'surface' },
                { key: 'launchBgColor',   label: 'Launch Button',  desc: 'Floating launcher',                 effective: launchBgColor || primaryColor, set: (v) => setLaunchBgColor(v),   reset: () => setLaunchBgColor(''),   isAuto: !launchBgColor,    group: 'surface' },
                { key: 'userBubbleColor', label: 'Visitor Bubble', desc: 'Visitor message background',        effective: userBubbleColor || primaryColor, set: (v) => setUserBubbleColor(v), reset: () => setUserBubbleColor(''), isAuto: !userBubbleColor, group: 'surface' },
                { key: 'botBubbleColor',  label: 'Bot Bubble',     desc: 'AI response background',            effective: botBubbleColor || '#f5f5f0',   set: (v) => setBotBubbleColor(v),  reset: () => setBotBubbleColor(''),  isAuto: !botBubbleColor,   group: 'surface' },
                { key: 'ctaColor',        label: 'CTA Buttons',    desc: 'Welcome CTA & action pill outline/fill', effective: ctaColor || primaryColor,  set: (v) => setCtaColor(v),        reset: () => setCtaColor(''),        isAuto: !ctaColor,         group: 'surface' },
                { key: 'suggestionColor', label: 'Suggestion Outline', desc: '"Ask about" chip outline & accent', effective: suggestionColor || primaryColor, set: (v) => setSuggestionColor(v), reset: () => setSuggestionColor(''), isAuto: !suggestionColor, group: 'surface' },
                { key: 'suggestionBgColor', label: 'Suggestion Fill', desc: '"Ask about" chip background',     effective: suggestionBgColor || 'transparent', set: (v) => setSuggestionBgColor(v), reset: () => setSuggestionBgColor(''), isAuto: !suggestionBgColor, group: 'surface' },
                // Text colors (ADA)
                { key: 'headerTextColor', label: 'Header Text',    desc: 'Assistant name (auto-contrast)',    effective: headerTextColor || '#ffffff',  set: (v) => setHeaderTextColor(v), reset: () => setHeaderTextColor(''), isAuto: !headerTextColor, group: 'text' },
                { key: 'botTextColor',    label: 'Bot Text',       desc: 'Assistant / welcome message text',  effective: botTextColor || '#1f2937',     set: (v) => setBotTextColor(v),    reset: () => setBotTextColor(''),    isAuto: !botTextColor,     group: 'text' },
                { key: 'linkColor',       label: 'Links',          desc: 'Hyperlinks in bot messages',        effective: linkColor || primaryColor,     set: (v) => setLinkColor(v),       reset: () => setLinkColor(''),       isAuto: !linkColor,        group: 'text' },
                { key: 'userTextColor',   label: 'Visitor Text',   desc: 'Visitor message text',              effective: userTextColor || '#ffffff',    set: (v) => setUserTextColor(v),   reset: () => setUserTextColor(''),   isAuto: !userTextColor,    group: 'text' },
                { key: 'ctaTextColor',    label: 'CTA Text',       desc: 'Pill button label (auto-contrast)', effective: ctaTextColor || '#1a1a1a',     set: (v) => setCtaTextColor(v),    reset: () => setCtaTextColor(''),    isAuto: !ctaTextColor,     group: 'text' },
                { key: 'suggestionTextColor', label: 'Suggestion Text', desc: '"Ask about" chip label',        effective: suggestionTextColor || suggestionColor || primaryColor, set: (v) => setSuggestionTextColor(v), reset: () => setSuggestionTextColor(''), isAuto: !suggestionTextColor, group: 'text' },
                { key: 'suggestionLabelColor', label: 'Suggestion Label', desc: '"Ask about" heading + icon',  effective: suggestionLabelColor || primaryColor, set: (v) => setSuggestionLabelColor(v), reset: () => setSuggestionLabelColor(''), isAuto: !suggestionLabelColor, group: 'text' },
              ];
              const activeSwatchData = swatches.find((s) => s.key === activePicker);
              const hasAnyCustom = swatches.some((s) => s.key !== 'primaryColor' && !s.isAuto);

              const openPickerFor = (key: string) => {
                if (activePicker === key) { setActivePicker(null); setPickerPos(null); return; }
                const rect = pickerBtnRefs.current[key]?.getBoundingClientRect();
                if (rect) {
                  const pickerH = 300;
                  const above = rect.bottom + pickerH > window.innerHeight - 20;
                  setPickerPos({ left: Math.min(rect.left, window.innerWidth - 240), top: above ? rect.top - pickerH - 4 : rect.bottom + 4, above });
                }
                setActivePicker(key);
              };

              const renderSwatch = (s: Swatch) => (
                <div key={s.key} className="flex flex-col gap-1">
                  {/* Fixed-height header; the Auto reset is absolutely positioned so it
                      never changes the row height or shifts the swatch below it. */}
                  <div className="relative flex h-4 items-center">
                    <p className="text-xs font-semibold text-gray-700">{s.label}</p>
                    {s.key !== 'primaryColor' && !s.isAuto && (
                      <button
                        type="button"
                        onClick={s.reset}
                        className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-0.5 p-0 leading-none text-[10px] text-gray-400 hover:text-gray-600"
                      >
                        <RotateCcw className="h-2.5 w-2.5" /> Auto
                      </button>
                    )}
                  </div>
                  <button
                    ref={(el) => { pickerBtnRefs.current[s.key] = el; }}
                    type="button"
                    onClick={() => openPickerFor(s.key)}
                    className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-left hover:border-gray-300 transition-colors w-full"
                  >
                    <span className="h-5 w-5 shrink-0 rounded-md border border-gray-200 shadow-sm" style={{ backgroundColor: s.effective }} />
                    {s.isAuto && s.key !== 'primaryColor'
                      ? <span className="text-[10px] font-medium text-gray-400 italic">Auto</span>
                      : <span className="text-xs text-gray-600 font-mono truncate">{s.effective}</span>}
                  </button>
                  <p className="text-[10px] text-gray-400 leading-tight">{s.desc}</p>
                </div>
              );

              return (
                <>
                  {hasAnyCustom && (
                    <div className="mb-4 flex items-center justify-between rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                      <div className="flex items-center gap-2 text-xs text-amber-700">
                        <Sparkles className="h-3.5 w-3.5 shrink-0" />
                        Some colors are pinned. Reset all to follow Primary / auto-contrast.
                      </div>
                      <button
                        type="button"
                        onClick={() => { swatches.filter((s) => s.key !== 'primaryColor').forEach((s) => s.reset()); }}
                        className="ml-3 shrink-0 flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-900"
                      >
                        <RotateCcw className="h-3 w-3" /> Reset all
                      </button>
                    </div>
                  )}

                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Surfaces</p>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {swatches.filter((s) => s.group === 'surface').map(renderSwatch)}
                  </div>

                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mt-5 mb-2">Text (ADA contrast)</p>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {swatches.filter((s) => s.group === 'text').map(renderSwatch)}
                  </div>

                  {activePicker && pickerPos && activeSwatchData && typeof document !== 'undefined' && createPortal(
                    <>
                      <div className="fixed inset-0 z-[999]" onClick={() => { setActivePicker(null); setPickerPos(null); }} />
                      <div
                        className="fixed z-[1000] shadow-2xl rounded-xl overflow-hidden"
                        style={{ top: pickerPos.top, left: pickerPos.left }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ChromePicker color={activeSwatchData.effective} onChange={(c) => activeSwatchData.set(c.hex)} disableAlpha />
                      </div>
                    </>,
                    document.body,
                  )}

                  <div className="mt-5">
                    <FormInput
                      label="Chat Height (px)"
                      type="number"
                      value={chatHeight}
                      onChange={(e) => setChatHeight(Number(e.target.value))}
                      placeholder="700"
                    />
                  </div>

                  {/* Online status toggle */}
                  <div className="mt-5 flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50/60 px-3.5 py-3">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-700">Show &quot;Online&quot; status</p>
                      <p className="text-[10px] text-gray-400 leading-tight mt-0.5">Green dot + label under the assistant name in the chat header.</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={showOnlineStatus}
                      onClick={() => setShowOnlineStatus((v) => !v)}
                      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${showOnlineStatus ? 'bg-primary-600' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${showOnlineStatus ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </>
              );
            })()}
          </SectionCard>

          {/* Typography */}
          <SectionCard
            icon={Type}
            title="Typography"
            subtitle="Font family and size for chat text. 'Use website font' auto-detects your site's font and applies it inside the chat widget."
          >
            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1.5">Font family</p>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {([
                    { value: '', label: 'System default', desc: 'OS native sans-serif' },
                    { value: 'website', label: 'Use website font', desc: 'Auto-detect from your site' },
                    { value: 'Inter', label: 'Inter', desc: 'Clean & modern' },
                    { value: 'Roboto', label: 'Roboto', desc: 'Google Material' },
                    { value: 'Poppins', label: 'Poppins', desc: 'Friendly & rounded' },
                    { value: 'Lato', label: 'Lato', desc: 'Humanist sans' },
                    { value: 'Open Sans', label: 'Open Sans', desc: 'Neutral & readable' },
                    { value: 'Montserrat', label: 'Montserrat', desc: 'Bold & geometric' },
                  ] as const).map(({ value, label, desc }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setFontFamily(value)}
                      className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-left transition-all ${
                        fontFamily === value
                          ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-400'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="min-w-0">
                        <p className={`text-xs font-semibold truncate ${fontFamily === value ? 'text-primary-700' : 'text-gray-800'}`}
                          style={{ fontFamily: value && value !== 'website' ? `'${value}', sans-serif` : undefined }}
                        >{label}</p>
                        <p className="text-[10px] text-gray-400 truncate">{desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Or type any Google Font name e.g. Nunito"
                  value={!['', 'website', 'Inter', 'Roboto', 'Poppins', 'Lato', 'Open Sans', 'Montserrat'].includes(fontFamily) ? fontFamily : ''}
                  onChange={(e) => setFontFamily(e.target.value)}
                  className="w-full h-9 rounded-md border border-gray-300 px-3 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormInput
                  label="Font Size (px)"
                  type="number"
                  value={fontSize}
                  onChange={(e) => setFontSize(Number(e.target.value))}
                  placeholder="16"
                />
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-1.5">Font weight</p>
                  <select
                    value={fontWeight}
                    onChange={(e) => setFontWeight(e.target.value)}
                    className="h-9 w-full rounded-md border border-gray-300 px-3 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                  >
                    <option value="">Default (400)</option>
                    <option value="300">Light (300)</option>
                    <option value="400">Regular (400)</option>
                    <option value="500">Medium (500)</option>
                    <option value="600">Semi-bold (600)</option>
                    <option value="700">Bold (700)</option>
                  </select>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* Icons */}
          <SectionCard
            icon={Images}
            title="Icons"
            subtitle="Avatars shown in the conversation and the launch button."
          >
            <div className='grid grid-cols-3 gap-3'>
              <ImageUploadField
                variant='card'
                label='Bot Chat Icon'
                helperText='Shown next to bot messages'
                src={botIcon}
                fileName={botIconFile?.name}
                allowSvg
                onSelect={(file, dataUrl) => { setBotIconSrc(dataUrl); setBotIconFile(file); }}
                onError={showIconError}
              />
              <ImageUploadField
                variant='card'
                label='User Chat Icon'
                helperText='Shown next to visitor messages'
                src={userIcon}
                fileName={userIconFile?.name}
                onSelect={(file, dataUrl) => { setUserIconSrc(dataUrl); setUserIconFile(file); }}
                onError={showIconError}
              />
              <ImageUploadField
                variant='card'
                label='Launch Icon'
                helperText='The button that opens the chat'
                src={launchIconSrc}
                fileName={launchIconFile?.name}
                onSelect={(file, dataUrl) => { setLaunchIconSrc(dataUrl); setLaunchIconFile(file); }}
                onError={showIconError}
              />
            </div>
          </SectionCard>

          {/* In-Chat Welcome Buttons */}
          <SectionCard
            icon={MousePointerClick}
            title="In-Chat Welcome Buttons"
            subtitle="Buttons that appear inside the open chat, just below your greeting — before the visitor sends their first message. Use these for high-value actions like demos, coverage checks, or guided conversations. They disappear once the visitor starts typing."
          >
            <CtaFlowsEditor flows={ctaFlows} onChange={setCtaFlows} />
          </SectionCard>
        </div>
      </PreviewTabLayout>
      <BotAlert message={alertMessage} show={alert} setShow={setAlert} />
    </>
  );
};

export default Customize;
